/**
 * CesiumJS のプリミティブ（地形メッシュ・箱・線）。サンプルの TERR_VS・terrFS・BOX_FS・gridGeometry・
 * meshPrimitive・Pool と同じ作り。CesiumJS を参照するのは src/render/cesium/ だけ（CSSP-SPEC-001 §7.2）。
 */
import * as C from 'cesium';
import type { Item, Rgb } from '../geometry';

const TERR_VS = `in vec3 position3DHigh;in vec3 position3DLow;in vec3 normal;in vec4 color;in float hgt;in float batchId;
out vec3 v_positionEC;out vec3 v_normalEC;out vec4 v_color;out float v_h;
void main(){vec4 p=czm_computePosition();v_positionEC=(czm_modelViewRelativeToEye*p).xyz;v_normalEC=czm_normal*normal;v_color=color;v_h=hgt;gl_Position=czm_modelViewProjectionRelativeToEye*p;}`;
const terrFS = (
  cont: boolean,
  ci: number,
) => `${cont ? `#define CONT\n#define CI ${ci.toFixed(1)}\n` : ''}in vec3 v_positionEC;in vec3 v_normalEC;in vec4 v_color;in float v_h;
void main(){vec3 n=normalize(v_normalEC);vec3 c=v_color.rgb;
#ifdef CONT
float q=v_h/CI;float w=fwidth(q);float f=abs(fract(q)-0.5);if(w>1e-5&&w<0.45&&f>0.5-w*1.15)c*=0.78;
#endif
float d1=max(dot(n,czm_lightDirectionEC),0.0);float d2=max(dot(n,-czm_lightDirectionEC),0.0);c*=0.56+0.46*d1+0.12*d2;out_FragColor=vec4(c,1.0);}`;
const BOX_FS = `in vec3 v_positionEC;in vec3 v_normalEC;in vec4 v_color;
void main(){vec3 n=normalize(v_normalEC);if(!gl_FrontFacing)n=-n;float d1=max(dot(n,czm_lightDirectionEC),0.0);float d2=max(dot(n,-czm_lightDirectionEC),0.0);
out_FragColor=vec4(v_color.rgb*(0.52+0.46*d1+0.16*d2),v_color.a);}`;

/** ローカル（y が上）→ ENU（z が上）。サンプルの P_YUP と逆の P_INV（列優先）。 */
export const P_YUP = new C.Matrix4(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1);
export const P_INV = new C.Matrix4(1, 0, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 0, 1);

let BOX: C.BoxGeometry | null = null;
let BOX_OUT: C.BoxOutlineGeometry | null = null;
const boxGeom = () =>
  (BOX ??= new C.BoxGeometry({
    minimum: new C.Cartesian3(-0.5, -0.5, 0),
    maximum: new C.Cartesian3(0.5, 0.5, 1),
    vertexFormat: C.PerInstanceColorAppearance.VERTEX_FORMAT,
  }));
const boxOutGeom = () =>
  (BOX_OUT ??= new C.BoxOutlineGeometry({
    minimum: new C.Cartesian3(-0.5, -0.5, 0),
    maximum: new C.Cartesian3(0.5, 0.5, 1),
  }));

const APP: Partial<Record<'edge' | 'solid' | 'trans', C.PerInstanceColorAppearance>> = {};
export function boxApp(kind: 'edge' | 'solid' | 'trans'): C.PerInstanceColorAppearance {
  return (APP[kind] ??=
    kind === 'edge'
      ? new C.PerInstanceColorAppearance({ flat: true, translucent: true })
      : new C.PerInstanceColorAppearance({
          translucent: kind === 'trans',
          closed: kind !== 'trans',
          fragmentShaderSource: BOX_FS,
        }));
}

const terrApp = new Map<string, C.Appearance>();
function terrainApp(cont: boolean, ci: number): C.Appearance {
  const k = `${cont}|${ci}`;
  let a = terrApp.get(k);
  if (!a) {
    a = new C.Appearance({
      translucent: false,
      closed: false,
      // 型定義に載っていない静的関数（サンプルと同じ。Cesium 1.145.0 に存在する）
      renderState: (
        C.Appearance as unknown as { getDefaultRenderState(t: boolean, c: boolean): object }
      ).getDefaultRenderState(false, false),
      vertexShaderSource: TERR_VS,
      fragmentShaderSource: terrFS(cont, ci),
    });
    terrApp.set(k, a);
  }
  return a;
}

/** 格子の地形（x, z は y が上のローカル。位置は ENU で入れ、modelMatrix に ENU→地球中心を渡す）。 */
export function gridGeometry(
  nx: number,
  nz: number,
  X: Float32Array,
  Z: Float32Array,
  Hh: Float32Array,
  COL: Float32Array,
  yRef: number,
  skirtBase: number,
  skirtCol: Rgb,
): C.Geometry {
  const W = nx + 1;
  const nv = W * (nz + 1);
  const ring: [number, number, number, number, number, number][] = [];
  for (let i = 0; i < nx; i++) ring.push([i, 0, i + 1, 0, 0, 1]);
  for (let j = 0; j < nz; j++) ring.push([nx, j, nx, j + 1, 1, 0]);
  for (let i = nx; i > 0; i--) ring.push([i, nz, i - 1, nz, 0, -1]);
  for (let j = nz; j > 0; j--) ring.push([0, j, 0, j - 1, -1, 0]);
  const tot = nv + ring.length * 4;
  const P = new Float64Array(tot * 3);
  const N = new Float32Array(tot * 3);
  const Cc = new Uint8Array(tot * 4);
  const Hg = new Float32Array(tot);
  const I: number[] = [];
  const dX = X[1]! - X[0]! || 1;
  const dZ = Z[W]! - Z[0]! || 1;
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) {
      const k = j * W + i;
      const i0 = Math.max(0, i - 1);
      const i1 = Math.min(nx, i + 1);
      const j0 = Math.max(0, j - 1);
      const j1 = Math.min(nz, j + 1);
      const hx = (Hh[j * W + i1]! - Hh[j * W + i0]!) / ((i1 - i0) * dX);
      const hz = (Hh[j1 * W + i]! - Hh[j0 * W + i]!) / ((j1 - j0) * dZ);
      const l = Math.hypot(hx, hz, 1);
      P[k * 3] = X[k]!;
      P[k * 3 + 1] = -Z[k]!;
      P[k * 3 + 2] = Hh[k]!;
      N[k * 3] = -hx / l;
      N[k * 3 + 1] = hz / l;
      N[k * 3 + 2] = 1 / l;
      Cc[k * 4] = COL[k * 3]! * 255;
      Cc[k * 4 + 1] = COL[k * 3 + 1]! * 255;
      Cc[k * 4 + 2] = COL[k * 3 + 2]! * 255;
      Cc[k * 4 + 3] = 255;
      Hg[k] = Hh[k]! + yRef;
    }
  }
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const a = j * W + i;
      I.push(a, a + 1, a + W, a + 1, a + W + 1, a + W);
    }
  }
  let v = nv;
  for (const [ia, ja, ib, jb, ox, on] of ring) {
    const a = ja * W + ia;
    const b = jb * W + ib;
    const base = v;
    for (const [src, bottom] of [
      [a, 0],
      [b, 0],
      [b, 1],
      [a, 1],
    ] as const) {
      P[v * 3] = P[src * 3]!;
      P[v * 3 + 1] = P[src * 3 + 1]!;
      P[v * 3 + 2] = bottom ? skirtBase : P[src * 3 + 2]!;
      N[v * 3] = ox;
      N[v * 3 + 1] = on;
      N[v * 3 + 2] = 0;
      Cc[v * 4] = skirtCol[0] * 255;
      Cc[v * 4 + 1] = skirtCol[1] * 255;
      Cc[v * 4 + 2] = skirtCol[2] * 255;
      Cc[v * 4 + 3] = 255;
      Hg[v] = (bottom ? skirtBase : P[src * 3 + 2]!) + yRef;
      v++;
    }
    I.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  return new C.Geometry({
    attributes: {
      position: new C.GeometryAttribute({
        componentDatatype: C.ComponentDatatype.DOUBLE,
        componentsPerAttribute: 3,
        values: P,
      }),
      normal: new C.GeometryAttribute({
        componentDatatype: C.ComponentDatatype.FLOAT,
        componentsPerAttribute: 3,
        values: N,
      }),
      color: new C.GeometryAttribute({
        componentDatatype: C.ComponentDatatype.UNSIGNED_BYTE,
        componentsPerAttribute: 4,
        normalize: true,
        values: Cc,
      }),
      hgt: new C.GeometryAttribute({
        componentDatatype: C.ComponentDatatype.FLOAT,
        componentsPerAttribute: 1,
        values: Hg,
      }),
    } as unknown as C.GeometryAttributes,
    indices: tot < 65536 ? new Uint16Array(I) : new Uint32Array(I),
    primitiveType: C.PrimitiveType.TRIANGLES,
    boundingSphere: C.BoundingSphere.fromVertices(Array.from(P)),
  });
}

export function meshPrimitive(
  enu: C.Matrix4,
  geom: C.Geometry,
  cont: boolean,
  ci: number,
): C.Primitive {
  return new C.Primitive({
    geometryInstances: new C.GeometryInstance({ geometry: geom }),
    appearance: terrainApp(cont, ci),
    modelMatrix: C.Matrix4.clone(enu),
    asynchronous: false,
    allowPicking: false,
    compressVertices: false,
    vertexCacheOptimize: false,
    interleave: false,
  });
}

const _m1 = new C.Matrix4();
const _m2 = new C.Matrix4();
/** 単位箱の行列（ローカル y が上）を地球中心座標へ。サンプルの itemMatrix。 */
export function itemMatrix(frame: C.Matrix4, m: Float32Array, res = new C.Matrix4()): C.Matrix4 {
  C.Matrix4.fromArray(Array.from(m), 0, _m1);
  C.Matrix4.multiply(frame, _m1, _m2);
  return C.Matrix4.multiply(_m2, P_INV, res);
}

/** 箱の集まりを、種類ごとに 1 つのプリミティブにまとめる。 */
export function boxesPrimitive(
  frame: C.Matrix4,
  items: readonly Item[],
  kind: 'edge' | 'solid' | 'trans',
): C.Primitive | null {
  if (!items.length) return null;
  const instances = items.map(
    (it) =>
      new C.GeometryInstance({
        geometry: kind === 'edge' ? boxOutGeom() : boxGeom(),
        modelMatrix: itemMatrix(frame, it.m),
        attributes: {
          color: C.ColorGeometryInstanceAttribute.fromColor(
            new C.Color(it.c[0], it.c[1], it.c[2], it.c[3]),
          ),
        },
      }),
  );
  return new C.Primitive({
    geometryInstances: instances,
    appearance: boxApp(kind),
    asynchronous: false,
    allowPicking: false,
    compressVertices: false,
  });
}

/** 毎フレーム位置の変わる箱（重機など）を使い回す。サンプルの Pool。 */
export class BoxPool {
  private readonly items: { prim: C.Primitive; c: number[]; dirty: boolean }[] = [];
  private n = 0;
  constructor(
    private readonly coll: C.PrimitiveCollection,
    private readonly translucent: boolean,
  ) {}
  begin(): void {
    this.n = 0;
  }
  add(frame: C.Matrix4, m: Float32Array, c: readonly number[]): void {
    let it = this.items[this.n];
    if (!it) {
      const p = new C.Primitive({
        geometryInstances: new C.GeometryInstance({
          geometry: boxGeom(),
          id: 'b',
          attributes: {
            color: C.ColorGeometryInstanceAttribute.fromColor(new C.Color(c[0], c[1], c[2], c[3])),
          },
        }),
        appearance: boxApp(this.translucent ? 'trans' : 'solid'),
        asynchronous: false,
        allowPicking: false,
        compressVertices: false,
      });
      it = { prim: this.coll.add(p) as C.Primitive, c: c.slice(), dirty: false };
      this.items.push(it);
    }
    this.n++;
    const p = it.prim;
    p.show = true;
    itemMatrix(frame, m, p.modelMatrix);
    if (it.c.some((v, i) => v !== c[i])) {
      it.c = c.slice();
      it.dirty = true;
    }
    if (it.dirty && p.ready) {
      try {
        const attrs = p.getGeometryInstanceAttributes('b') as { color: Uint8Array };
        attrs.color = C.ColorGeometryInstanceAttribute.toValue(new C.Color(c[0], c[1], c[2], c[3]));
        it.dirty = false;
      } catch {
        // まだ準備ができていないときは次のフレームで直す
      }
    }
  }
  end(): void {
    for (let i = this.n; i < this.items.length; i++) this.items[i]!.prim.show = false;
  }
}
